import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ADMIN_PROPERTIES_PAGE_SIZE,
  buildPropertiesBrowseRequest,
  buildPropertyLookupRequest,
  listAdminProperties,
} from '../../services/admin/propertiesService';
import { classifyAdminCallableError } from './adminUserDisplay';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminOwnerPrimaryLabel,
  adminPropertyListPrice,
  canViewPropertyFinancials,
  formatAdminDate,
  formatAdminMoney,
  formatAdminPropertyAddress,
} from './adminPropertyDisplay';

const TEXT_LOOKUP_TYPES = ['email', 'ownerName', 'address', 'city', 'postalCode'];

function CardField({ label, children }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-800">{children}</dd>
    </div>
  );
}

function PropertyRowActions({ property, t }) {
  return (
    <div className="flex flex-col items-start gap-1">
      <Link
        to={`/admin/properties/${encodeURIComponent(property.id)}`}
        className="font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        {t('admin.properties.open360')}
      </Link>
    </div>
  );
}

function OwnerCell({ owner, t }) {
  const label = adminOwnerPrimaryLabel(owner);
  const uid = typeof owner?.uid === 'string' && owner.uid.trim() ? owner.uid.trim() : '';
  const email = typeof owner?.email === 'string' ? owner.email.trim() : '';
  const showEmail = Boolean(email && label !== email);
  if (!uid) {
    return (
      <div>
        <p>{label}</p>
        {showEmail ? <p className="break-all text-xs text-slate-500">{email}</p> : null}
      </div>
    );
  }
  return (
    <div>
      <Link
        to={`/admin/users/${encodeURIComponent(uid)}`}
        className="font-medium text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        {label}
      </Link>
      {showEmail ? <p className="break-all text-xs text-slate-500">{email}</p> : null}
      <Link
        to={`/admin/users/${encodeURIComponent(uid)}`}
        className="mt-0.5 block text-xs font-semibold text-indigo-600 hover:underline"
      >
        {t('admin.properties.viewOwner')}
      </Link>
    </div>
  );
}

function PriceCell({ item, language, t }) {
  const price = adminPropertyListPrice(item);
  if (price.kind == null) return ADMIN_DATE_PLACEHOLDER;
  const label =
    price.kind === 'purchase' ? t('admin.properties.price.purchase') : t('admin.properties.price.asking');
  return (
    <span title={label}>
      {formatAdminMoney(price.value, language)}
      <span className="ml-1 text-[11px] uppercase tracking-wide text-slate-500">{label}</span>
    </span>
  );
}

export default function AdminPropertiesPage() {
  const { t, language } = useLanguage();
  const { adminRole } = useAuth();
  const role = adminRole || 'super_admin';
  const showPrice = canViewPropertyFinancials(role);
  const [searchType, setSearchType] = useState('email');
  const [searchInput, setSearchInput] = useState('');
  const [unitsMin, setUnitsMin] = useState('');
  const [unitsMax, setUnitsMax] = useState('');
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [mode, setMode] = useState('browse');
  const [properties, setProperties] = useState([]);
  const [status, setStatus] = useState('loading');
  const [errorKind, setErrorKind] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [tokenStack, setTokenStack] = useState([]);
  const [currentToken, setCurrentToken] = useState(null);
  const [activeLookup, setActiveLookup] = useState(null);
  const [searchTruncated, setSearchTruncated] = useState(false);

  const busy = status === 'loading' || status === 'paging';
  const canSubmit =
    searchType === 'units'
      ? String(unitsMin).trim() !== '' || String(unitsMax).trim() !== ''
      : searchType === 'createdAt'
        ? Boolean(createdAfter || createdBefore)
        : Boolean(searchInput.trim());

  const applyList = (data, nextMode, { paging = false, nextStack, pageToken } = {}) => {
    const list = Array.isArray(data?.properties) ? data.properties : [];
    setProperties(list);
    setMode(nextMode);
    setNextPageToken(data?.nextPageToken || null);
    setCurrentToken(pageToken || null);
    setSearchTruncated(Boolean(data?.searchMeta?.truncated));
    if (Array.isArray(nextStack)) setTokenStack(nextStack);
    else if (!paging) setTokenStack([]);
    setStatus(list.length === 0 ? (nextMode === 'browse' ? 'empty' : 'lookup-empty') : 'ready');
  };

  const fail = (err) => {
    setProperties([]);
    setNextPageToken(null);
    setSearchTruncated(false);
    setErrorKind(classifyAdminCallableError(err));
    setStatus('error');
  };

  const loadBrowse = async (pageToken, { paging = false, nextStack } = {}) => {
    setStatus(paging ? 'paging' : 'loading');
    setErrorKind(null);
    setActiveLookup(null);
    try {
      const data = await listAdminProperties(
        buildPropertiesBrowseRequest({ pageSize: ADMIN_PROPERTIES_PAGE_SIZE, pageToken }),
      );
      applyList(data, 'browse', { paging, nextStack, pageToken });
    } catch (err) {
      fail(err);
    }
  };

  const loadLookup = async (lookup, pageToken, { paging = false, nextStack } = {}) => {
    setStatus(paging ? 'paging' : 'loading');
    setErrorKind(null);
    try {
      const data = await listAdminProperties(
        buildPropertyLookupRequest({
          ...lookup,
          pageSize: ADMIN_PROPERTIES_PAGE_SIZE,
          pageToken,
        }),
      );
      setActiveLookup(lookup);
      applyList(data, 'lookup', { paging, nextStack, pageToken });
    } catch (err) {
      fail(err);
    }
  };

  useEffect(() => {
    loadBrowse(undefined);
  }, []);

  const lookupFromForm = () => {
    if (searchType === 'units') {
      return {
        lookupType: 'units',
        unitsMin: unitsMin === '' ? undefined : unitsMin,
        unitsMax: unitsMax === '' ? undefined : unitsMax,
      };
    }
    if (searchType === 'createdAt') {
      return {
        lookupType: 'createdAt',
        createdAfter,
        createdBefore,
      };
    }
    return { lookupType: searchType, [searchType]: searchInput.trim() };
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setTokenStack([]);
    setCurrentToken(null);
    await loadLookup(lookupFromForm());
  };

  const handleReset = () => {
    setSearchInput('');
    setSearchType('email');
    setUnitsMin('');
    setUnitsMax('');
    setCreatedAfter('');
    setCreatedBefore('');
    setTokenStack([]);
    setCurrentToken(null);
    setActiveLookup(null);
    setErrorKind(null);
    setSearchTruncated(false);
    loadBrowse(undefined);
  };

  const canPage = (mode === 'browse' || mode === 'lookup') && !busy;

  const handleNext = () => {
    if (!canPage || !nextPageToken) return;
    const nextStack = [...tokenStack, currentToken];
    if (mode === 'browse') {
      loadBrowse(nextPageToken, { paging: true, nextStack });
      return;
    }
    if (activeLookup) {
      loadLookup(activeLookup, nextPageToken, { paging: true, nextStack });
    }
  };

  const handlePrevious = () => {
    if (!canPage || tokenStack.length === 0) return;
    const nextStack = tokenStack.slice(0, -1);
    const previousToken = tokenStack[tokenStack.length - 1];
    if (mode === 'browse') {
      loadBrowse(previousToken || undefined, { paging: true, nextStack });
      return;
    }
    if (activeLookup) {
      loadLookup(activeLookup, previousToken || undefined, { paging: true, nextStack });
    }
  };

  const errorMessage =
    errorKind === 'unavailable'
      ? t('admin.properties.error.unavailable')
      : errorKind === 'internal'
        ? t('admin.properties.error.internal')
        : errorKind === 'permission-denied'
          ? t('admin.properties.error.permission')
          : errorKind === 'invalid-argument'
            ? t('admin.properties.error.invalid')
            : errorKind === 'failed-precondition'
              ? t('admin.properties.error.index')
              : t('admin.properties.error');

  const showPagination =
    (mode === 'browse' || mode === 'lookup') &&
    (status === 'ready' || status === 'paging' || status === 'empty' || status === 'lookup-empty');

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex items-start gap-2">
        <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-900">{t('admin.properties.title')}</h1>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
              {t('admin.properties.readOnly')}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-600">{t('admin.properties.subtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSearch} className="flex flex-col gap-2 lg:flex-row lg:items-end">
        <label className="text-sm lg:w-56">
          <span className="mb-1 block text-slate-600">{t('admin.properties.search.type')}</span>
          <select
            id="admin-properties-search-type"
            value={searchType}
            onChange={(event) => setSearchType(event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="email">{t('admin.properties.search.type.email')}</option>
            <option value="ownerName">{t('admin.properties.search.type.ownerName')}</option>
            <option value="address">{t('admin.properties.search.type.address')}</option>
            <option value="city">{t('admin.properties.search.type.city')}</option>
            <option value="postalCode">{t('admin.properties.search.type.postalCode')}</option>
            <option value="units">{t('admin.properties.search.type.units')}</option>
            <option value="createdAt">{t('admin.properties.search.type.createdAt')}</option>
          </select>
        </label>
        {TEXT_LOOKUP_TYPES.includes(searchType) ? (
          <label className="min-w-0 flex-1 text-sm">
            <span className="mb-1 block text-slate-600">{t('admin.properties.search.label')}</span>
            <input
              id="admin-properties-search"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t(`admin.properties.search.placeholder.${searchType}`)}
              autoComplete="off"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </label>
        ) : null}
        {searchType === 'units' ? (
          <div className="flex min-w-0 flex-1 gap-2">
            <label className="min-w-0 flex-1 text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.properties.search.units.min')}</span>
              <input
                id="admin-properties-search-units-min"
                type="number"
                min="0"
                max="999"
                step="1"
                value={unitsMin}
                onChange={(event) => setUnitsMin(event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="min-w-0 flex-1 text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.properties.search.units.max')}</span>
              <input
                id="admin-properties-search-units-max"
                type="number"
                min="0"
                max="999"
                step="1"
                value={unitsMax}
                onChange={(event) => setUnitsMax(event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
          </div>
        ) : null}
        {searchType === 'createdAt' ? (
          <div className="flex min-w-0 flex-1 gap-2">
            <label className="min-w-0 flex-1 text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.properties.search.createdAfter')}</span>
              <input
                id="admin-properties-search-created-after"
                type="date"
                value={createdAfter}
                onChange={(event) => setCreatedAfter(event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="min-w-0 flex-1 text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.properties.search.createdBefore')}</span>
              <input
                id="admin-properties-search-created-before"
                type="date"
                value={createdBefore}
                onChange={(event) => setCreatedBefore(event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={busy || !canSubmit}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.properties.search.submit')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={busy && status === 'loading'}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.properties.reset')}
          </button>
        </div>
      </form>

      {status === 'loading' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.properties.loading')}
        </p>
      ) : null}
      {status === 'paging' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.properties.paging')}
        </p>
      ) : null}
      {status === 'empty' ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.properties.empty')}
        </p>
      ) : null}
      {status === 'lookup-empty' ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.properties.lookupEmpty')}
        </p>
      ) : null}
      {searchTruncated && (status === 'ready' || status === 'paging' || status === 'lookup-empty') ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {t('admin.properties.search.truncated')}
        </p>
      ) : null}
      {status === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{errorMessage}</p>
          <button
            type="button"
            onClick={() =>
              mode === 'lookup' && activeLookup
                ? loadLookup(activeLookup, currentToken || undefined)
                : loadBrowse(currentToken || undefined)
            }
            className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {t('admin.properties.retry')}
          </button>
        </div>
      ) : null}

      {properties.length > 0 && (status === 'ready' || status === 'paging') ? (
        <>
          <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white md:block">
            <table className="min-w-full text-left text-sm">
              <caption className="sr-only">{t('admin.properties.title')}</caption>
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.properties.col.building')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.properties.col.owner')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.properties.col.units')}</th>
                  {showPrice ? (
                    <th scope="col" className="px-3 py-2 font-semibold">{t('admin.properties.col.price')}</th>
                  ) : null}
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.properties.col.brokers')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.properties.col.created')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.properties.col.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2">
                      <p className="font-medium text-slate-900">{formatAdminPropertyAddress(item) || ADMIN_DATE_PLACEHOLDER}</p>
                      <p className="break-all text-xs text-slate-500">{item.id}</p>
                    </td>
                    <td className="px-3 py-2">
                      <OwnerCell owner={item.owner} t={t} />
                    </td>
                    <td className="px-3 py-2">{item.numberOfUnits ?? ADMIN_DATE_PLACEHOLDER}</td>
                    {showPrice ? (
                      <td className="px-3 py-2">
                        <PriceCell item={item} language={language} t={t} />
                      </td>
                    ) : null}
                    <td className="px-3 py-2">{Number(item.brokerCount) || 0}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatAdminDate(item.createTime, language)}</td>
                    <td className="px-3 py-2">
                      <PropertyRowActions property={item} t={t} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {properties.map((item) => (
              <article key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
                <p className="font-semibold text-slate-900">
                  {formatAdminPropertyAddress(item) || ADMIN_DATE_PLACEHOLDER}
                </p>
                <p className="mt-0.5 break-all text-xs text-slate-500">{item.id}</p>
                <dl className="mt-3 grid grid-cols-2 gap-3">
                  <CardField label={t('admin.properties.col.owner')}>
                    <OwnerCell owner={item.owner} t={t} />
                  </CardField>
                  <CardField label={t('admin.properties.col.units')}>
                    {item.numberOfUnits ?? ADMIN_DATE_PLACEHOLDER}
                  </CardField>
                  {showPrice ? (
                    <CardField label={t('admin.properties.col.price')}>
                      <PriceCell item={item} language={language} t={t} />
                    </CardField>
                  ) : null}
                  <CardField label={t('admin.properties.col.brokers')}>
                    {Number(item.brokerCount) || 0}
                  </CardField>
                  <CardField label={t('admin.properties.col.created')}>
                    {formatAdminDate(item.createTime, language)}
                  </CardField>
                </dl>
                <div className="mt-3">
                  <PropertyRowActions property={item} t={t} />
                </div>
              </article>
            ))}
          </div>
        </>
      ) : null}

      {showPagination ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={busy || tokenStack.length === 0}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.properties.previous')}
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={busy || !nextPageToken}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.properties.next')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
